import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=logged out, object=logged in
  const [error, setError] = useState("");

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // 2FA gate — caller continues with the challenge_id flow.
      if (data?.requires_2fa) {
        return { ok: false, requires_2fa: true, challenge_id: data.challenge_id, email: data.email };
      }
      setUser({ id: data.id, email: data.email, name: data.name, has_base_resume: false });
      await fetchMe();
      return { ok: true };
    } catch (e) {
      const status = e.response?.status;
      const retryAfter = parseInt(e.response?.headers?.["retry-after"] || "0", 10) || 0;
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(detail);
      return { ok: false, status, retryAfter, detail };
    }
  };

  const verify2fa = async (challenge_id, code) => {
    try {
      const { data } = await api.post("/auth/2fa/verify", { challenge_id, code });
      setUser({ id: data.id, email: data.email, name: data.name, has_base_resume: false });
      await fetchMe();
      return { ok: true };
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      return { ok: false, detail };
    }
  };

  const register = async (name, email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      setUser({ id: data.id, email: data.email, name: data.name, has_base_resume: false });
      await fetchMe();
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, verify2fa, register, logout, error, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
